import React from 'react';
import { Send, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { formatExpiry } from './documentConstants';

export const PublishTemplateDialog = ({
  open,
  onOpenChange,
  template,
  clients,
  publishMode,
  setPublishMode,
  selectedClients,
  toggleClientSelect,
  addendumSummary,
  setAddendumSummary,
  publishing,
  onPublish,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Send className="h-5 w-5 text-amber-500" />
          Publish: {template?.title}
        </DialogTitle>
        <DialogDescription className="sr-only">Publish: {template?.title} dialog. Use the form below to continue.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-800">
              <p>Publishing will send the document to clients for acknowledgment. Clients who have already acknowledged a previous version will receive an addendum.</p>
              {template?.expires_in_days && (
                <p className="mt-1">Documents will expire <strong>{formatExpiry(template.expires_in_days)}</strong> after acknowledgment.</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <Label>Publish To</Label>
          <Select value={publishMode} onValueChange={setPublishMode}>
            <SelectTrigger data-testid="publish-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Active Clients</SelectItem>
              <SelectItem value="select">Select Specific Clients</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {publishMode === 'select' && (
          <div className="border rounded-lg max-h-48 overflow-y-auto">
            {clients.map((client) => (
              <label
                key={client.id}
                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={selectedClients.includes(client.id)}
                  onChange={() => toggleClientSelect(client.id)}
                  className="rounded border-gray-300"
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{client.company_name}</p>
                  <p className="text-xs text-gray-400">{client.account_number}</p>
                </div>
              </label>
            ))}
          </div>
        )}

        <div>
          <Label>Addendum Summary <span className="text-gray-400 font-normal">(optional)</span></Label>
          <Textarea
            value={addendumSummary}
            onChange={(e) => setAddendumSummary(e.target.value)}
            placeholder="Brief description of changes made..."
            rows={3}
            data-testid="addendum-summary-input"
          />
        </div>
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button
          className="bg-amber-500 hover:bg-amber-600 text-white"
          onClick={onPublish}
          disabled={publishing}
          data-testid="confirm-publish-btn"
        >
          {publishing ? 'Publishing...' : `Publish to ${publishMode === 'all' ? 'All Clients' : `${selectedClients.length} Client(s)`}`}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
